import { useEffect, useRef, useState } from "react";

/**
 * Keeps visited panels mounted (hidden) so revisiting a tab does not reload it.
 * Uses display toggle — no absolute positioning — so flex height stays correct.
 * Re-triggers enter animation whenever activeKey changes (without remounting panel state).
 *
 * `keepAlive(key)` limits that to some panels: the others are mounted only while
 * active, so each visit starts them fresh. Without it, every visited panel is kept.
 */
export default function WindowNavPanels({ activeKey, renderPanel, className = "", keepAlive }) {
  const [visited, setVisited] = useState(() => new Set([activeKey]));
  const panelRefs = useRef(new Map());

  useEffect(() => {
    setVisited((prev) => {
      if (prev.has(activeKey)) return prev;
      const next = new Set(prev);
      next.add(activeKey);
      return next;
    });
  }, [activeKey]);

  useEffect(() => {
    const el = panelRefs.current.get(activeKey);
    if (!el) return undefined;
    el.classList.remove("mint-window-panel-active");
    // Force reflow so the animation restarts on every tab switch
    void el.offsetWidth;
    el.classList.add("mint-window-panel-active");
    return undefined;
  }, [activeKey, visited]);

  return (
    <div className={`flex min-h-0 flex-1 flex-col ${className}`}>
      {[...visited].map((key) => {
        const isActive = activeKey === key;
        if (!isActive && keepAlive && !keepAlive(key)) return null;
        return (
          <div
            key={key}
            ref={(node) => {
              if (node) panelRefs.current.set(key, node);
              else panelRefs.current.delete(key);
            }}
            className={`campaign-window-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto${
              isActive ? "" : " hidden"
            }`}
            aria-hidden={!isActive}
          >
            {renderPanel(key)}
          </div>
        );
      })}
    </div>
  );
}
